import { useState, useEffect, useRef } from 'react';
import {
  Images, Sparkles, RefreshCw,
  ShieldCheck, Trash2, FlaskConical, TriangleAlert,
} from 'lucide-react';
import { scGet, scPost } from '@/lib/scFetch';
import {
  Resource, OversizedLogMatch, JobState, OptimizeSettings,
  INITIAL_JOB_STATE, DEFAULT_SETTINGS,
} from './types';
import FolderListView    from './FolderListView';
import FilesDrillView    from './FilesDrillView';
import ProgressStrip     from './ProgressStrip';
import TestingChecklist  from './TestingChecklist';

type View = 'folders' | 'files';

export default function OptimizeTexturesPage() {
  const [resources,     setResources]     = useState<Resource[]>([]);
  const [oversizedLogs, setOversizedLogs] = useState<OversizedLogMatch[]>([]);
  const [loadingRes,    setLoadingRes]    = useState(false);
  const [loadingLogs,   setLoadingLogs]   = useState(false);

  const [jobState, setJobState] = useState<JobState>(INITIAL_JOB_STATE);

  const [view,        setView]        = useState<View>('folders');
  const [drillTarget, setDrillTarget] = useState<Resource | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

  const [selectedPath, setSelectedPath] = useState('');
  const [settings,     setSettings]     = useState<OptimizeSettings>(DEFAULT_SETTINGS);

  const [testingOver,    setTestingOver]    = useState<boolean | null>(null);
  const [showChecklist,  setShowChecklist]  = useState(false);
  const [testerName,     setTesterName]     = useState('');
  const [nameInput,      setNameInput]      = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);

  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchResources();
    pollProgress();
    scGet<{ testingOver: boolean }>('/fivem/toolkitv/testing-status')
      .then(d => setTestingOver(d.testingOver))
      .catch(() => setTestingOver(true));
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [jobState.logs]);

  useEffect(() => {
    const isActive = jobState.active
      || jobState.status === 'running'
      || jobState.status === 'starting';
    if (!isActive) return;
    const timer = setInterval(pollProgress, 1500);
    return () => clearInterval(timer);
  }, [jobState.active, jobState.status]);

  const fetchResources = async () => {
    setLoadingRes(true);
    try {
      const data = await scGet<{ resources: Resource[] }>('/fivem/toolkitv/resources');
      setResources(data.resources ?? []);
    } catch (err) {
      console.error('Failed to fetch resources', err);
    } finally {
      setLoadingRes(false);
    }
  };

  const scanServerLogs = async () => {
    setLoadingLogs(true);
    try {
      const data = await scGet<{ oversized: OversizedLogMatch[] }>('/fivem/toolkitv/scan-logs');
      setOversizedLogs(data.oversized ?? []);
    } catch (err) {
      console.error('Failed to scan server logs', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const pollProgress = async () => {
    try {
      const state = await scGet<JobState>('/fivem/toolkitv/progress');
      setJobState(state);
      if (state.status === 'success' || state.status === 'failed' || state.status === 'cancelled') {
        fetchResources();
      }
    } catch (err) {
      console.error('Failed to poll progress', err);
    }
  };

  const startOptimization = async (pathOverride?: string, filesOverride?: string[]) => {
    const targetPath = pathOverride !== undefined ? pathOverride : selectedPath;
    try {
      setJobState(prev => ({ ...prev, active: true, status: 'starting', logs: ['Starting job request...'] }));
      const body: Record<string, unknown> = {
        path:          targetPath,
        downsize:      settings.downsize,
        format:        settings.format,
        onlyOversized: settings.onlyOversized,
        sizeThreshold: settings.sizeThreshold,
        backupRoot:    settings.backupRoot,
      };
      if (filesOverride && filesOverride.length > 0) body.files = filesOverride;
      const res = await scPost<{ ok: boolean }>('/fivem/toolkitv/optimize', body);
      if (res.ok) pollProgress();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start job';
      setJobState(prev => ({ ...prev, active: false, status: 'failed', error: msg }));
    }
  };

  const cancelOptimization = async () => {
    try {
      await scPost<{ ok: boolean }>('/fivem/toolkitv/cancel', {});
      pollProgress();
    } catch (err) {
      console.error('Failed to cancel', err);
    }
  };

  const handleDrillIn = (resource: Resource) => {
    setDrillTarget(resource);
    setSelectedFiles(new Set());
    setView('files');
  };

  const handleBack = () => {
    setDrillTarget(null);
    setSelectedFiles(new Set());
    setView('folders');
  };

  const showJobProgress = jobState.active || jobState.status !== 'idle';

  const handleHelpTest = () => {
    if (testerName) { setShowChecklist(true); return; }
    setShowNamePrompt(true);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ background: '#090b0f' }}>

      {/* Testing phase disclaimer banner */}
      {testingOver === false && (
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-amber-500/20 shrink-0"
             style={{ background: 'rgba(245,158,11,0.06)' }}>
          <div className="flex items-center gap-2.5 min-w-0">
            <TriangleAlert size={14} className="text-amber-400 shrink-0" />
            <p className="text-[10.5px] font-bold text-amber-300 leading-tight">
              <span className="uppercase tracking-wider text-amber-400">⚠ TEST BEFORE TRYING —</span>
              {' '}This feature has not been fully tested yet. Use on a test server only. Backups are created automatically, but proceed with caution.
            </p>
          </div>
          <button
            onClick={handleHelpTest}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#00ffd1]/20 bg-[#00ffd1]/[0.06] text-[9.5px] font-black text-[#00ffd1] uppercase tracking-wider hover:bg-[#00ffd1]/10 transition-all shrink-0"
          >
            <FlaskConical size={11} /> Help Test
          </button>
        </div>
      )}

      {/* Tester name prompt modal */}
      {showNamePrompt && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="rounded-2xl border border-white/[0.08] p-6 flex flex-col gap-4 w-[320px]" style={{ background: '#0d1018' }}>
            <div className="flex items-center gap-2 text-[12px] font-black text-[#00ffd1] uppercase">
              <FlaskConical size={15} /> Texture Optimizer Test
            </div>
            <p className="text-[10.5px] text-slate-400 leading-relaxed">
              Thanks for helping test! Enter your Discord username so your results are attributed correctly.
            </p>
            <input
              autoFocus
              type="text"
              placeholder="Your Discord username..."
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && nameInput.trim()) {
                  setTesterName(nameInput.trim());
                  setShowNamePrompt(false);
                  setShowChecklist(true);
                }
              }}
              className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-2.5 text-[11px] text-white font-mono focus:outline-none focus:border-[#00ffd1]/40"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowNamePrompt(false)} className="flex-1 py-2 rounded-xl border border-white/[0.08] text-[10px] text-slate-400 font-bold hover:bg-white/[0.04] transition-all">
                Cancel
              </button>
              <button
                disabled={!nameInput.trim()}
                onClick={() => {
                  setTesterName(nameInput.trim());
                  setShowNamePrompt(false);
                  setShowChecklist(true);
                }}
                className="flex-1 py-2 rounded-xl font-black text-[10px] text-slate-950 disabled:opacity-40 hover:opacity-90 transition-all uppercase"
                style={{ background: 'linear-gradient(90deg,#00ffd1,#a78bfa)' }}
              >
                Start Testing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating checklist panel */}
      {showChecklist && testingOver === false && (
        <TestingChecklist testerName={testerName} onClose={() => setShowChecklist(false)} />
      )}

      {/* Reopen pill — shown when checklist is hidden but testing still active */}
      {!showChecklist && testingOver === false && testerName && (
        <button
          onClick={() => setShowChecklist(true)}
          style={{ bottom: 24, right: 24, zIndex: 9990 }}
          className="fixed flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#00ffd1]/20 bg-[#090b0f] shadow-xl text-[10px] font-black text-[#00ffd1] uppercase tracking-wider hover:bg-[#00ffd1]/[0.08] transition-all"
        >
          <FlaskConical size={12} /> Checklist
        </button>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.04] shrink-0">
        <div>
          <h1 className="text-xs font-black tracking-[0.1em] text-white uppercase flex items-center gap-2">
            <Images size={16} className="text-[#00ffd1]" style={{ filter: 'drop-shadow(0 0 8px rgba(0,255,209,0.4))' }} />
            Texture Optimizer
          </h1>
          <p className="text-[9.5px] font-semibold text-slate-500 mt-1 uppercase tracking-wider">
            Lock: C:\Users\Public\Desktop\FiveM\files\resources
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchResources}
            disabled={loadingRes || jobState.active}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[10px] font-semibold text-slate-300 hover:bg-white/[0.05] disabled:opacity-50 transition-all"
          >
            <RefreshCw size={11} className={loadingRes ? 'animate-spin' : ''} />
            Reload Resources
          </button>
          <button
            onClick={scanServerLogs}
            disabled={loadingLogs}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-purple-500/20 bg-purple-500/[0.05] text-[10px] font-semibold text-purple-300 hover:bg-purple-500/10 transition-all"
          >
            <Sparkles size={11} />
            Scan Boot Logs
          </button>
        </div>
      </div>

      {/* Progress strip — shown over both views when job active/done */}
      {showJobProgress && <ProgressStrip jobState={jobState} />}

      {/* VIEW: Folders */}
      {view === 'folders' && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto flex flex-col gap-5">

            {/* Boot log alerts */}
            {oversizedLogs.length > 0 && (
              <div className="rounded-2xl border border-purple-500/20 p-5 space-y-3"
                   style={{ background: 'linear-gradient(90deg,rgba(167,139,250,0.03),rgba(167,139,250,0.06))' }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-bold text-purple-300 flex items-center gap-2 uppercase tracking-wide">
                    <Sparkles size={13} className="text-[#a78bfa]" />
                    Found {oversizedLogs.length} Oversized Texture Warnings in Boot Logs
                  </h3>
                  <button onClick={() => setOversizedLogs([])} className="text-[10px] text-slate-500 hover:text-slate-300 font-bold uppercase">Clear</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {oversizedLogs.map(item => (
                    <div key={item.relativePath} className="flex items-center justify-between bg-black/40 border border-purple-500/10 p-3 rounded-xl gap-4">
                      <div className="overflow-hidden">
                        <div className="text-[11px] font-bold text-white truncate">{item.name}</div>
                        <div className="text-[9px] text-slate-400 mt-0.5 font-mono">{item.relativePath}</div>
                        <div className="text-[8.5px] font-semibold text-purple-400 mt-1 uppercase">{item.oversizedFiles.length} oversized file(s)</div>
                      </div>
                      <button
                        onClick={() => startOptimization(item.relativePath)}
                        disabled={jobState.active}
                        className="px-3 py-1.5 bg-[#a78bfa] text-slate-950 font-bold text-[9px] rounded-lg hover:bg-purple-300 transition-all shrink-0 disabled:opacity-50"
                      >
                        Optimize
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Folder list + settings */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <FolderListView
                  resources={resources}
                  loading={loadingRes}
                  selectedPath={selectedPath}
                  jobActive={jobState.active}
                  onSelect={setSelectedPath}
                  onDrillIn={handleDrillIn}
                  onQuickOptimize={path => startOptimization(path)}
                />
              </div>

              {/* Settings sidebar */}
              <div className="lg:col-span-1 flex flex-col gap-5">
                <div className="rounded-2xl border border-white/[0.05] p-5 flex flex-col gap-4" style={{ background: '#11141c' }}>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white border-b border-white/[0.04] pb-3 block">
                    Optimize Settings
                  </span>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">Target Directory</label>
                      <div className="bg-black/40 border border-white/[0.04] rounded-lg px-2.5 py-2 font-mono text-[9.5px] text-[#00ffd1] truncate">
                        {selectedPath ? `/resources/${selectedPath}` : 'ALL FOLDERS (ROOT)'}
                      </div>
                      {selectedPath && (
                        <button onClick={() => setSelectedPath('')} className="text-[8.5px] font-semibold text-red-400 hover:text-red-300 uppercase mt-1 flex items-center gap-0.5">
                          <Trash2 size={9} /> Reset to root / Scan all
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase text-slate-500 block mb-1">Downsize Dimension Limit</label>
                      <input
                        type="number"
                        value={settings.sizeThreshold}
                        onChange={e => setSettings(s => ({ ...s, sizeThreshold: e.target.value }))}
                        disabled={jobState.active}
                        className="w-full bg-black/40 border border-white/[0.06] rounded-lg p-2 text-xs text-white focus:outline-none focus:border-[#00ffd1] disabled:opacity-50"
                        placeholder="e.g. 2048"
                      />
                      <span className="text-[8px] text-slate-600 mt-1 block">Sum of width + height. Default 2048 (1024×1024).</span>
                    </div>
                    <div className="space-y-2">
                      {(
                        [
                          { key: 'downsize'      as const, label: 'Downsize textures above limit' },
                          { key: 'format'        as const, label: 'Format optimization (DXT1/DXT5)' },
                          { key: 'onlyOversized' as const, label: 'Only modify oversized assets (>16MB)' },
                        ] as Array<{ key: keyof Pick<OptimizeSettings, 'downsize' | 'format' | 'onlyOversized'>; label: string }>
                      ).map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={settings[key]}
                            onChange={e => setSettings(s => ({ ...s, [key]: e.target.checked }))}
                            disabled={jobState.active}
                            className="rounded accent-[#00ffd1]"
                          />
                          <span className="text-[11px] text-slate-300">{label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="border-t border-white/[0.04] pt-3">
                      <button
                        onClick={() => startOptimization()}
                        disabled={jobState.active}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-black text-[10px] text-slate-950 hover:opacity-90 disabled:opacity-50 transition-all uppercase"
                        style={{ background: 'linear-gradient(90deg,#00ffd1,#a78bfa)' }}
                      >
                        Launch Texture Optimization
                      </button>
                    </div>
                  </div>
                </div>

                {/* Backup card */}
                <div className="rounded-2xl border border-[#a78bfa]/20 p-5 space-y-2 bg-[#a78bfa]/[0.02]">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#a78bfa] uppercase">
                    <ShieldCheck size={13} /> Backup Directory
                  </div>
                  <p className="text-[9px] text-slate-400 leading-relaxed">All texture modifications are non-destructive. Copy original .ytd files to:</p>
                  <input
                    type="text"
                    value={settings.backupRoot}
                    onChange={e => setSettings(s => ({ ...s, backupRoot: e.target.value }))}
                    disabled={jobState.active}
                    className="w-full bg-black/40 border border-[#a78bfa]/20 rounded-lg p-2 font-mono text-[8.5px] text-[#a78bfa] focus:outline-none focus:border-[#a78bfa] disabled:opacity-50"
                  />
                  <p className="text-[8.5px] text-slate-600 italic">A subfolder is created per run with date, time, and your admin handle.</p>
                </div>

                {/* Console log (folder view, when job active) */}
                {showJobProgress && (
                  <div className="rounded-2xl border border-white/[0.05] p-5 flex flex-col gap-4" style={{ background: '#11141c' }}>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Optimizer Console</span>
                    <div className="bg-black/60 border border-black/80 rounded-xl p-3 font-mono text-[10px] text-slate-300 overflow-y-auto space-y-0.5" style={{ minHeight: 200, maxHeight: 260 }}>
                      {jobState.logs.map((line, i) => (
                        <div key={i} className="whitespace-pre-wrap leading-relaxed py-0.5 border-b border-white/[0.01]">
                          <span className="text-slate-700 mr-1">[{i + 1}]</span>{line}
                        </div>
                      ))}
                      <div ref={logEndRef} />
                    </div>
                    {jobState.active && (
                      <button onClick={cancelOptimization} className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-red-500/30 bg-red-500/[0.05] text-red-400 font-bold text-[10px] hover:bg-red-500/15 transition-all uppercase">
                        Cancel Process
                      </button>
                    )}
                    {!jobState.active && jobState.status !== 'idle' && (
                      <button onClick={() => setJobState(INITIAL_JOB_STATE)} className="w-full py-2 rounded-xl border border-white/[0.08] bg-white/[0.02] text-slate-300 font-bold text-[10px] hover:bg-white/[0.05] transition-all uppercase">
                        Dismiss
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW: Files (full-width drill-in) */}
      {view === 'files' && drillTarget && (
        <FilesDrillView
          resource={drillTarget}
          jobState={jobState}
          selectedFiles={selectedFiles}
          settings={settings}
          onSelectionChange={setSelectedFiles}
          onSettingsChange={patch => setSettings(s => ({ ...s, ...patch }))}
          onOptimizeAll={() => startOptimization(drillTarget.relativePath)}
          onOptimizeSelected={() => startOptimization(drillTarget.relativePath, [...selectedFiles])}
          onCancel={cancelOptimization}
          onRunAgain={() => setJobState(INITIAL_JOB_STATE)}
          onBack={handleBack}
        />
      )}
    </div>
  );
}
